const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const formatDate = (value) => {
  if (!value) return 'date pending';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'date pending';
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const buildStewardAction = (label, tab, roomId = 'steward-the-work', toolContext = null) => ({
  label,
  tab,
  roomId,
  toolContext,
});

const getRetentionTone = (retentionRate) => {
  if (typeof retentionRate !== 'number') return 'steady';
  if (retentionRate < 60) return 'attention';
  if (retentionRate < 75) return 'next';
  return 'steady';
};

const getRetentionLabel = (retentionRate) => {
  if (typeof retentionRate !== 'number') return 'still loading';
  if (retentionRate < 60) return 'under pressure';
  if (retentionRate < 75) return 'worth watching';
  return 'holding steady';
};

export function buildStewardTheWorkRoomContext({
  room,
  analytics = null,
  atRiskMembers = [],
  pricingCoupons = [],
  primaryAction,
  secondaryAction,
}) {
  const retentionRate = analytics?.retention?.retention_rate;
  const newMembers = analytics?.overview?.new_members || 0;
  const activeCoupons = pricingCoupons.filter((coupon) => coupon?.is_active !== false);
  const retentionTone = getRetentionTone(retentionRate);

  const holdingSteadyItems = [
    typeof retentionRate === 'number'
      ? {
          id: 'steward-retention',
          eyebrow: 'Retention read',
          title: `${retentionRate}% retention is ${getRetentionLabel(retentionRate)}`,
          detail: retentionRate < 60
            ? 'This is not yet a calm durability number, but it gives you the clearest place to stabilize first.'
            : 'Staying power is strong enough that you can make careful business moves instead of reacting out of fear.',
          tone: retentionTone,
          location: 'In Analytics',
          action: buildStewardAction('Open Analytics', 'analytics'),
        }
      : null,
    ...activeCoupons.slice(0, 2).map((coupon) => ({
      id: coupon.coupon_id || coupon.code || coupon.title || 'coupon',
      eyebrow: 'Pricing lever live',
      title: coupon?.title || coupon?.code || 'Active offer',
      detail: [
        coupon?.discount_type === 'percentage'
          ? `${coupon.discount_value}% off`
          : coupon?.discount_value
            ? `$${coupon.discount_value} off`
            : null,
        coupon?.max_uses ? `${coupon.uses_count || 0}/${coupon.max_uses} uses` : 'Unlimited uses',
      ].filter(Boolean).join(' · '),
      tone: 'next',
      location: 'In Pricing',
      action: buildStewardAction('Open Pricing', 'pricing'),
    })),
    newMembers > 0
      ? {
          id: 'steward-new-members',
          eyebrow: 'Fresh movement',
          title: `${pluralize(newMembers, 'new member')} joined in this window`,
          detail: 'New movement matters, but only if the operation is stable enough to hold what is growing.',
          tone: 'steady',
          location: 'In Analytics',
          action: buildStewardAction('Open Analytics', 'analytics'),
        }
      : null,
  ].filter(Boolean).slice(0, 4);

  const needsYourMoveItems = [
    ...atRiskMembers.slice(0, 3).map((member, index) => ({
      id: member?.user_id || member?.id || member?.email || `at-risk-${index}`,
      eyebrow: member?.risk_level ? `${member.risk_level} risk` : 'At-risk dad',
      title: member?.name || member?.email || 'Unnamed dad',
      detail: member?.last_active === 'Never'
        ? 'Has not meaningfully engaged yet and may need a more human intervention.'
        : member?.days_inactive
          ? `Last active ${member.days_inactive} days ago`
          : 'Needs a closer retention look.',
      tone: 'attention',
      location: 'In Analytics',
      action: buildStewardAction('Open Analytics', 'analytics'),
    })),
    activeCoupons.length === 0
      ? {
          id: 'steward-no-offers',
          eyebrow: 'Pricing gap',
          title: 'No active pricing levers are live right now',
          detail: 'That may be fine, but it means there is no current offer you can reach for if durability starts softening.',
          tone: typeof retentionRate === 'number' && retentionRate < 75 ? 'attention' : 'next',
          location: 'In Pricing',
          action: buildStewardAction('Open Pricing', 'pricing'),
        }
      : null,
  ].filter(Boolean).slice(0, 4);

  const suggestedActions = [];

  if (atRiskMembers.length > 0) {
    suggestedActions.push({
      tone: 'attention',
      eyebrow: 'Do first',
      title: `Review ${pluralize(atRiskMembers.length, 'at-risk dad')}`,
      detail: 'Handle the clearest retention pressure before it quietly turns into losses you could have seen earlier.',
      location: 'In Analytics',
      action: buildStewardAction('Open Analytics', 'analytics'),
    });
  }

  if (activeCoupons.length === 0) {
    suggestedActions.push({
      tone: atRiskMembers.length > 0 ? 'next' : 'attention',
      eyebrow: atRiskMembers.length > 0 ? 'Then' : 'Do first',
      title: 'Decide whether pricing needs a live lever',
      detail: 'If durability is softening, a clear pricing or retention lever may be the next stabilizing move.',
      location: 'In Pricing',
      action: buildStewardAction('Open Pricing', 'pricing'),
    });
  } else {
    suggestedActions.push({
      tone: 'steady',
      eyebrow: atRiskMembers.length > 0 ? 'Then' : 'Do first',
      title: 'Review which active offer is still worth keeping',
      detail: 'A live offer should support the mission on purpose, not just linger because it exists.',
      location: 'In Pricing',
      action: buildStewardAction('Open Pricing', 'pricing'),
    });
  }

  if (!suggestedActions.length && typeof retentionRate === 'number') {
    suggestedActions.push({
      tone: retentionTone,
      eyebrow: 'Watch',
      title: `Keep watching ${retentionRate}% retention`,
      detail: 'This is a good moment to stay close to sustainability without forcing a bigger move than the numbers require.',
      location: 'In Business Health',
      action: buildStewardAction('Open Analytics', 'analytics'),
    });
  }

  const statusTone = atRiskMembers.length > 0
    ? 'attention'
    : retentionTone === 'attention'
      ? 'attention'
      : activeCoupons.length > 0 || typeof retentionRate === 'number'
        ? 'next'
        : 'steady';

  const statusLabel = atRiskMembers.length > 0
    ? 'Needs attention'
    : activeCoupons.length === 0 && typeof retentionRate === 'number' && retentionRate < 75
      ? 'Stabilize next'
      : typeof retentionRate === 'number'
        ? 'Steady'
        : 'Build next';

  return {
    id: room.id,
    variant: 'steward-the-work',
    statusTone,
    statusLabel,
    startTitle: atRiskMembers.length > 0
      ? 'Protect retention before chasing more growth'
      : activeCoupons.length === 0 && typeof retentionRate === 'number' && retentionRate < 75
        ? 'Stabilize durability before pressure gets louder'
        : 'Check whether the business is staying healthy enough to sustain the mission',
    startDetail: 'This room should make sustainability feel calm and actionable by keeping retention risk, durability, and pricing levers in one place.',
    signals: [
      {
        tone: retentionTone,
        text: typeof retentionRate === 'number'
          ? `Retention is ${retentionRate}% right now`
          : 'Retention will surface here once analytics loads.',
      },
      {
        tone: atRiskMembers.length > 0 ? 'attention' : 'steady',
        text: atRiskMembers.length > 0
          ? `${pluralize(atRiskMembers.length, 'dad')} are currently flagged for churn risk`
          : 'No dads are currently flagged for churn risk.',
      },
      {
        tone: activeCoupons.length > 0 ? 'next' : 'steady',
        text: activeCoupons.length > 0
          ? `${pluralize(activeCoupons.length, 'active offer')} are available as pricing levers`
          : 'No active pricing levers are currently live.',
      },
    ],
    metrics: [
      { label: 'Retention', value: typeof retentionRate === 'number' ? `${retentionRate}%` : '—', detail: 'Current read on staying power', tone: retentionTone },
      { label: 'At-risk dads', value: atRiskMembers.length, detail: 'People most likely to slip without intervention', tone: atRiskMembers.length > 0 ? 'attention' : 'steady' },
      { label: 'Active offers', value: activeCoupons.length, detail: 'Pricing levers currently available', tone: activeCoupons.length > 0 ? 'next' : 'steady' },
    ],
    holdingSteadyItems,
    needsYourMoveItems,
    suggestedActions,
    primaryAction,
    secondaryAction,
  };
}
